import React, { useEffect, useRef, useState } from 'react';

import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import classNames from 'classnames';
import _ from 'lodash';
import ReactQuill from 'react-quill';
import * as qs from 'query-string';
import { validator, isValid, errors } from 'react-validator';

import {
  useInsertDiscussionContentMutation,
  useUpdateMySubscriptionMutation,
} from '@school_ify/schoolify-repository/src/shared/types';

import { useAppSelector } from 'redux/hooks';
import usePathName from 'hooks/usePathName';
import { getUser } from 'redux/authentication/auth.slice';
import Message from 'components/Icons/Message';
import ArrowDown from 'components/Icons/ArrowDown';
import './Details.scoped.scss';
import Bell from 'components/Icons/Bell';
import Button from 'clean/components/common/Button/Button';
import Errors from 'components/Errors/Errors';
import useQueryParams from 'hooks/useQueryParams';

import ProfilePicture from '../../../../../clean/components/common/ProfilePicture/ProfilePicture';

const Details: React.FC<{ discussion; update; isSubscribed; subscriptionUpdate }> = ({
  discussion,
  update,
  isSubscribed,
  subscriptionUpdate,
}) => {
  const { t, i18n } = useTranslation('study');
  const user = useAppSelector(getUser);
  const query_data = qs.parse(window.location.search);
  const hasDiscussionId = discussion.id === query_data.discussion_id;
  const [isOpened, setIsOpened] = useState<boolean>(hasDiscussionId);
  const [openTyper, setOpenTyper] = useState<boolean>();
  const reactQuillRef = useRef(null);

  const history = useHistory();
  const path = usePathName();

  const [updateMySubscriptionMutation] = useUpdateMySubscriptionMutation();
  const [insertDiscussionContentMutation] = useInsertDiscussionContentMutation();
  const [validationErrors, setValidationErrors] = useState<any>({});

  const queryData = useQueryParams();

  const [model, setModel] = useState({
    content: '',
    parent: {
      id: null,
      title: '',
    },
  });

  async function updateSubscription() {
    await updateMySubscriptionMutation({
      variables: {
        id: isSubscribed.id,
        is_notification_enabled: !isSubscribed.is_notification_enabled,
      },
    });

    subscriptionUpdate();
  }

  async function save() {
    setValidationErrors({});
    validator(
      model,
      {
        content: ['required'],
      },
      {
        'content.required': t('common:content_field_not_blank'),
      }
    );
    setValidationErrors(errors());
    if (!isValid()) {
      return;
    }
    await insertDiscussionContentMutation({
      variables: {
        content: model.content,
        user_id: user.id,
        assignment_discussion_id: discussion.id,
        parent_id: model.parent.id,
      },
    });
    update();
    setOpenTyper(false);
    setIsOpened(true);
  }

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ],
  };

  useEffect(() => {
    if (reactQuillRef.current) reactQuillRef.current.focus();
  }, [openTyper]);

  const openDiscussion = () => {
    setIsOpened(!isOpened);
    if (!isOpened) {
      queryData.discussion_id = discussion.id;
    } else {
      queryData.discussion_id = null;
    }
    history.push({
      pathname: path,
      search: Object.keys(queryData)
        .map(function (key) {
          return `${key}=${queryData[key]}`;
        })
        .join('&'),
    });
  };

  const onMapLoad = () => {
    const { section, discussion_id } = qs.parse(window.location.search);
    if (section === 'discussion' && discussion_id !== 'null') {
      window.scrollTo({
        left: 0,
        top: document.getElementById(discussion_id as string).offsetTop,
      });
    }
  };

  const getUserFullName = obj => {
    return obj?.student == null ? obj?.teacher?.user?.full_name : obj?.student?.user?.full_name;
  };

  const getDiscussionReplies = () => {
    const discussionContents = discussion.assignment_discussion_contents;
    const replies = discussionContents.slice(1, discussionContents.size);
    return replies;
  };

  return (
    <div className="container border-radius-10px" key={discussion.id}>
      <div className="horizontal-line"></div>
      <div className="flex justify-between align-center">
        <div className="title-wrapper">
          <div className="icon-wrapper">
            <Message color="var(--colour-secondary)" />
          </div>
          <div className="startedby-wrapper">
            <h4
              key={`${discussion.id}_title`}
              className="cursor-pointer"
              id={discussion.id}
              onClick={openDiscussion}
            >
              {discussion.title}
            </h4>
            <div>
              <h5 className="flex separate">
                <span> {t('Started by')}</span>
                <span>{getUserFullName(_.last(discussion.assignment_discussion_contents))}</span>
                &#x2027;{' '}
                <span onClick={openDiscussion}>
                  {discussion.assignment_discussion_contents.length} Replies
                </span>
              </h5>
            </div>
          </div>
        </div>
        <div className="dropdown-wrapper flex justify-between">
          {isSubscribed && (
            <div onClick={updateSubscription} className="bell-icon">
              <Bell
                fontSize="1.35em"
                color={isSubscribed.is_notification_enabled ? '#5cd79e' : '#F04F48'}
              />
            </div>
          )}
          <div className="dropdown-content">
            <div
              onClick={openDiscussion}
              className={classNames('arrow', { 'rotate-180deg': !isOpened })}
            >
              <ArrowDown />
            </div>

            <p className="flex separate">
              <span>Last reply</span>
              <span>
                {DateTime.fromISO(discussion?.assignment_discussion_contents[0]?.created_at)
                  .setLocale(i18n.language)
                  .toRelative()}
              </span>
            </p>
          </div>
        </div>
      </div>
      {isOpened && (
        <div className="opened-discussion-container">
          <div className="info-about-poster">
            <div className="user-info">
              <div className="profile-pic">
                <ProfilePicture userId={discussion?.user_id} />
              </div>
              <h4 className="flex separate">
                <span className="element">Started by</span>
                <span className="element">
                  {getUserFullName(discussion?.assignment_discussion_contents[0])}
                </span>{' '}
                on
                <span className="element">
                  {DateTime.fromISO(discussion?.assignment_discussion_contents[0]?.created_at)
                    .setLocale(i18n.language)
                    .toISODate()}
                </span>
              </h4>
            </div>
            <div className="started-when">
              <h4 className="flex separate">
                <span>Started</span>
                <span>
                  {DateTime.fromISO(discussion?.assignment_discussion_contents[0]?.created_at)
                    .setLocale(i18n.language)
                    .toFormat('hh:mm')}
                </span>
              </h4>
            </div>
          </div>
          <div
            className="initial-post"
            dangerouslySetInnerHTML={{
              __html: discussion?.assignment_discussion_contents[0]?.content,
            }}
          ></div>
          {getDiscussionReplies().map(obj => {
            return (
              <div className="bg-white m-1 border-radius-10px pa-1" key={obj.id} onLoad={onMapLoad}>
                <div className="reply-profile">
                  <div className="reply-inside-container">
                    <div className="reply-profile-pic">
                      <ProfilePicture userId={obj.user_id} />
                    </div>
                    <h1>{getUserFullName(obj)}</h1>
                  </div>
                  <div className="reply-labels">
                    <label htmlFor={''} className="text-small date-reply flex separate">
                      <span>Replied</span>
                      {DateTime.fromISO(obj?.created_at).setLocale(i18n.language).toRelative()}
                    </label>
                  </div>
                </div>
                <div
                  className="content-reply"
                  dangerouslySetInnerHTML={{ __html: obj?.content }}
                ></div>
              </div>
            );
          })}
          {openTyper ? (
            <div
              key={`${discussion.id}_items`}
              className="flex flex-col bg-white pa-1 m-1 border-radius-10px"
            >
              <label htmlFor={''} className="text-small">
                Replay to:
                <span className="text-bold pl-1">
                  {getUserFullName(discussion.assignment_discussion_contents[0])}
                </span>
              </label>
              <ReactQuill
                style={{ border: 'none', margin: '10px', borderRadius: '10px' }}
                theme="snow"
                value={model.content}
                modules={modules}
                ref={reactQuillRef}
                onChange={val => {
                  setModel({
                    ...model,
                    content: val,
                  });
                }}
              />
              <Errors errors={validationErrors} prop="content" />
              <div className="flex mt-1 justify-end align-center gap-2">
                <label
                  className="cursor-pointer"
                  onClick={() => {
                    setModel({
                      content: '',
                      parent: {
                        id: null,
                        title: '',
                      },
                    });
                    setOpenTyper(false);
                  }}
                >
                  {t('common:cancel')}
                </label>
                <label
                  className="btn bg-primary text-white rounded-full nowrap  no-underline text-bold"
                  onClick={() => {
                    save();
                  }}
                >
                  {t('common:save')}
                </label>
              </div>
            </div>
          ) : (
            <div className="label-input">
              <Button
                onClick={() => setOpenTyper(event => !event)}
                label={t('study:Reply')}
                color="primary"
                variant="filled"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Details;
